import { useState } from 'react';
import { backdropClose } from '../../utils/backdropClose';
import {
    Check,
    X,
    Zap,
    Shield,
    Maximize,
    Minimize,
    BarChart3,
    Users2,
    Globe,
    Clock,
    Calendar,
    Brain,
    Calculator,
    Smartphone,
    Fingerprint,
    MessageSquare
} from 'lucide-react';

const GST_PERCENT = 18;
const BASE_MEMBER_LIMIT = 300;
const EXTRA_BLOCK_SIZE = 100;
const EXTRA_BLOCK_YEARLY_PRICE = 500;
const INCLUDED_CREDITS_VALUE = 1000;
const UTILITY_MSG_RATE = 0.5;
const MARKETING_MSG_RATE = 0.8;

interface PricingFeature {
    text: string;
    included: boolean;
    isNew?: boolean;
}

interface PricingPlan {
    id: string;
    name: string;
    tagline: string;
    description: string;
    monthlyPrice: number;
    features: PricingFeature[];
    ai?: boolean;
}

const AiStar = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <defs>
            <linearGradient id="ai-star-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#aad816" />
                <stop offset="100%" stopColor="#5eead4" />
            </linearGradient>
        </defs>
        <path
            fill="url(#ai-star-grad)"
            d="M12 2c.4 3.2 1.3 5.4 2.9 7.1C16.6 10.7 18.8 11.6 22 12c-3.2.4-5.4 1.3-7.1 2.9C13.3 16.6 12.4 18.8 12 22c-.4-3.2-1.3-5.4-2.9-7.1C7.4 13.3 5.2 12.4 2 12c3.2-.4 5.4-1.3 7.1-2.9C10.7 7.4 11.6 5.2 12 2z"
        />
        <path
            fill="url(#ai-star-grad)"
            d="M19 2.5c.15 1.2.5 2 1.1 2.65.6.6 1.45.95 2.65 1.1-1.2.15-2 .5-2.65 1.1-.6.6-.95 1.45-1.1 2.65-.15-1.2-.5-2-1.1-2.65-.6-.6-1.45-.95-2.65-1.1 1.2-.15 2-.5 2.65-1.1.6-.65.95-1.45 1.1-2.65z"
        />
    </svg>
);

const PRICING_PLANS: PricingPlan[] = [
    {
        id: 'pro',
        name: 'Pro',
        tagline: 'Run your gym',
        description: 'The complete management suite for professional fitness centers.',
        monthlyPrice: 425,
        features: [
            { text: 'Dashboard & Analytics', included: true },
            { text: 'Attendance Scanning', included: true },
            { text: 'Membership Management', included: true },
            { text: 'Purchase History & Billing', included: true },
            { text: 'Individual Member CRM', included: true },
            { text: 'Automated Notifications', included: true },
            { text: 'Multi-branch Support', included: true },
            { text: 'Team & Staff Management', included: true },
            { text: 'Marketing Campaigns', included: true },
            { text: 'Reach AI — Daily Follow-up Queue', included: false },
            { text: 'Smart Follow-up Prioritization', included: false },
        ],
    },
    {
        id: 'pro-ai',
        name: 'Pro AI',
        tagline: 'Grow with intelligence',
        description: 'Everything in Pro, supercharged with AI-driven growth tools.',
        monthlyPrice: 545,
        ai: true,
        features: [
            { text: 'Everything in Pro', included: true },
            { text: 'Reach AI — Daily Follow-up Queue', included: true, isNew: true },
            { text: 'Renewal, Trial & Lead Prioritization', included: true },
            { text: 'Smart Scoring with Reason Chips', included: true },
            { text: 'WhatsApp Read/Delivery Intelligence', included: true },
            { text: 'One-tap Follow-up Actions', included: true },
            { text: 'Priority WhatsApp Support', included: true },
        ],
    },
];

interface AddOn {
    id: string;
    name: string;
    description: string;
    yearlyPrice: number;
    icon: JSX.Element;
}

const ADD_ONS: AddOn[] = [
    {
        id: 'mobile',
        name: 'Member Mobile App',
        description: 'Branded mobile experience for your members — plans, attendance, payments.',
        yearlyPrice: 2000,
        icon: <Smartphone className="w-6 h-6 text-[#aad816]" />,
    },
    {
        id: 'biometrics',
        name: 'Biometric Access',
        description: 'Fingerprint / biometric device integration for automated check-ins.',
        yearlyPrice: 1000,
        icon: <Fingerprint className="w-6 h-6 text-[#aad816]" />,
    },
];

const WHY_GYMPE = [
    {
        icon: <BarChart3 className="w-6 h-6 text-[#aad816]" />,
        title: "Intelligent Analytics",
        description: "Real-time insights into member retention, revenue growth, and branch performance."
    },
    {
        icon: <Users2 className="w-6 h-6 text-[#aad816]" />,
        title: "Unified Member CRM",
        description: "A single source of truth for all member interactions across all your branches."
    },
    {
        icon: <Zap className="w-6 h-6 text-[#aad816]" />,
        title: "Automated Workflows",
        description: "Automate renewals, follow-ups, and notifications with built-in WhatsApp API."
    },
    {
        icon: <Shield className="w-6 h-6 text-[#aad816]" />,
        title: "Secure & Scalable",
        description: "Cloud-native architecture designed to grow from one gym to a nationwide chain."
    }
];

const THE_ROADMAP = [
    {
        icon: <Globe className="w-5 h-5 text-[#aad816]" />,
        date: "Q4 2026",
        title: "Gympe Network",
        description: "Allow your members to access premium partner gyms across India when traveling city-to-city."
    },
    {
        icon: <Clock className="w-5 h-5 text-[#aad816]" />,
        date: "COMING SOON",
        title: "Dynamic Strategic Pricing",
        description: "Identify peak hours and set intelligent off-peak pricing to maximize occupancy and revenue."
    },
    {
        icon: <Calendar className="w-5 h-5 text-[#aad816]" />,
        date: "IN DEVELOPMENT",
        title: "Advanced Slot Booking",
        description: "Built-in training schedules and class registration for seamless session management."
    },
    {
        icon: <Brain className="w-5 h-5 text-[#aad816]" />,
        date: "AI PILOT",
        title: "Predictive AI Analytics",
        description: "Member churn prediction and personalized engagement strategies powered by AI."
    }
];

const yearlyBase = (plan: PricingPlan) => plan.monthlyPrice * 12;

const extraMemberBlocks = (members: number) =>
    members > BASE_MEMBER_LIMIT ? Math.ceil((members - BASE_MEMBER_LIMIT) / EXTRA_BLOCK_SIZE) : 0;

const PricingCard = ({ plan, onOpenCalculator }: { plan: PricingPlan; onOpenCalculator: () => void }) => {
    return (
        <div className="relative flex flex-col p-8 rounded-[3rem] transition-all duration-500 hover:scale-[1.02] bg-white text-zinc-900 border border-zinc-100 shadow-xl">
            <div className="mb-8">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2 text-zinc-400">
                    {plan.tagline}
                </p>
                <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-3xl font-black tracking-tight">{plan.name}</h3>
                    {plan.ai && <AiStar className="w-7 h-7 drop-shadow-[0_0_6px_rgba(170,216,22,0.5)]" />}
                </div>
                <p className="text-sm font-medium leading-relaxed text-zinc-500">
                    {plan.description}
                </p>
            </div>

            {/* Price */}
            <div className="p-6 rounded-[2rem] mb-8 border bg-zinc-50 border-zinc-100">
                <div className="flex items-baseline gap-1.5">
                    <span className="text-4xl font-black tracking-tighter">₹{plan.monthlyPrice}</span>
                    <span className="text-xs font-bold text-zinc-400">/mo</span>
                </div>
                <p className="mt-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                    Billed yearly · ₹{yearlyBase(plan).toLocaleString()}/yr
                </p>
                <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest text-zinc-300">
                    + {GST_PERCENT}% GST
                </p>

                <div className="mt-4 pt-4 border-t space-y-2 border-zinc-200/60">
                    <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600">
                        Up to {BASE_MEMBER_LIMIT} members
                    </p>
                    <p className="text-[10px] font-bold text-zinc-400">
                        +₹{EXTRA_BLOCK_YEARLY_PRICE}/yr for every {EXTRA_BLOCK_SIZE} members thereafter
                    </p>
                    <p className="text-[10px] font-black text-[#aad816]">
                        ₹{INCLUDED_CREDITS_VALUE.toLocaleString()} notification credits loaded
                    </p>
                </div>
            </div>

            <div className="flex-1 space-y-4">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 text-zinc-300">
                    Core Features:
                </p>
                {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                        <div className={`mt-0.5 p-0.5 rounded-full ${feature.included ? 'text-emerald-500' : 'text-zinc-200'}`}>
                            {feature.included ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className={`text-sm font-semibold tracking-tight ${feature.included ? 'text-zinc-700' : 'text-zinc-300'}`}>
                                {feature.text}
                            </span>
                            {feature.isNew && (
                                <span className="px-1.5 py-0.5 bg-[#aad816] text-black text-[7px] font-black rounded uppercase tracking-widest">New</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <button
                onClick={onOpenCalculator}
                className="mt-8 w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 bg-black text-white hover:bg-zinc-800"
            >
                <Calculator className="w-4 h-4" />
                Estimate my cost
            </button>
        </div>
    );
};

const numOrZero = (val: number | "") => (val === "" ? 0 : val);

const NumberInput = ({
    value,
    onChange,
    className = "",
}: {
    value: number | "";
    onChange: (val: number | "") => void;
    className?: string;
}) => (
    <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => {
            const val = e.target.value;
            if (val === "") onChange("");
            else if (!isNaN(parseInt(val))) onChange(Math.max(0, parseInt(val)));
        }}
        className={`bg-white border border-zinc-200 rounded-xl py-2.5 px-4 font-black text-lg text-black focus:ring-4 focus:ring-[#aad816]/20 outline-none transition-all ${className}`}
    />
);

const PlanCostLane = ({
    plan,
    memberCount,
    selectedAddOns,
    utilityMsgs,
    marketingMsgs,
}: {
    plan: PricingPlan;
    memberCount: number;
    selectedAddOns: string[];
    utilityMsgs: number;
    marketingMsgs: number;
}) => {
    const blocks = extraMemberBlocks(memberCount);
    const base = yearlyBase(plan);
    const extraMembersCost = blocks * EXTRA_BLOCK_YEARLY_PRICE;
    const activeAddOns = ADD_ONS.filter(a => selectedAddOns.includes(a.id));
    const addOnsCost = activeAddOns.reduce((sum, a) => sum + a.yearlyPrice, 0);
    const messagesCost = utilityMsgs * UTILITY_MSG_RATE + marketingMsgs * MARKETING_MSG_RATE;

    const subtotal = base + extraMembersCost + addOnsCost + messagesCost;
    const gstAmount = subtotal * (GST_PERCENT / 100);
    const total = subtotal + gstAmount;

    return (
        <div className="flex flex-col rounded-[2rem] border overflow-hidden bg-zinc-50 border-zinc-100 text-zinc-900">
            <div className="p-5 pb-4 border-b border-zinc-200/60">
                <div className="flex items-center gap-2">
                    <h4 className="text-xl font-black tracking-tight">{plan.name}</h4>
                    {plan.ai && <AiStar className="w-5 h-5 drop-shadow-[0_0_5px_rgba(170,216,22,0.5)]" />}
                </div>
                <p className="text-[10px] font-bold mt-0.5 text-zinc-400">
                    ₹{plan.monthlyPrice}/mo · billed yearly
                </p>
            </div>

            <div className="flex-1 p-5 space-y-2.5">
                <div className="flex justify-between items-center gap-2">
                    <p className="text-[11px] font-bold text-zinc-500">Subscription (yearly)</p>
                    <p className="text-xs font-black whitespace-nowrap">₹{base.toLocaleString()}</p>
                </div>
                {extraMembersCost > 0 && (
                    <div className="flex justify-between items-center gap-2">
                        <p className="text-[11px] font-bold text-zinc-500">Extra members ({blocks} × {EXTRA_BLOCK_SIZE})</p>
                        <p className="text-xs font-black whitespace-nowrap">₹{extraMembersCost.toLocaleString()}</p>
                    </div>
                )}
                {activeAddOns.map(a => (
                    <div key={a.id} className="flex justify-between items-center gap-2">
                        <p className="text-[11px] font-bold text-zinc-500">{a.name}</p>
                        <p className="text-xs font-black whitespace-nowrap">₹{a.yearlyPrice.toLocaleString()}</p>
                    </div>
                ))}
                <div className="flex justify-between items-center gap-2">
                    <p className="text-[11px] font-bold text-zinc-500">Credits loaded</p>
                    <p className="text-xs font-black text-emerald-500 whitespace-nowrap">₹{INCLUDED_CREDITS_VALUE.toLocaleString()} incl.</p>
                </div>
                {messagesCost > 0 && (
                    <div className="flex justify-between items-center gap-2">
                        <p className="text-[11px] font-bold text-zinc-500">Additional messages</p>
                        <p className="text-xs font-black whitespace-nowrap">₹{messagesCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    </div>
                )}
                <div className="flex justify-between items-center gap-2 pt-2.5 border-t border-zinc-200">
                    <p className="text-[11px] font-black uppercase tracking-wider">Subtotal</p>
                    <p className="text-sm font-black whitespace-nowrap">₹{subtotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                </div>
                <div className="flex justify-between items-center gap-2">
                    <p className="text-[11px] font-bold text-zinc-400">GST @ {GST_PERCENT}%</p>
                    <p className="text-xs font-black whitespace-nowrap text-zinc-400">₹{gstAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                </div>
            </div>

            <div className="p-5 bg-black text-white">
                <p className="text-[8px] font-black uppercase tracking-widest mb-1 text-zinc-500">Total / yr · incl. GST</p>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-black tracking-tighter">₹{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <p className="mt-1 text-[9px] font-bold text-zinc-500">
                    ≈ ₹{Math.round(total / 12).toLocaleString()}/mo effective
                </p>
            </div>
        </div>
    );
};

const PricingCalculatorModal = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const [members, setMembers] = useState<number | "">(300);
    const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
    const [utilityMsgs, setUtilityMsgs] = useState<number | "">(0);
    const [marketingMsgs, setMarketingMsgs] = useState<number | "">(0);

    if (!isOpen) return null;

    const memberCount = numOrZero(members);
    const blocks = extraMemberBlocks(memberCount);

    const toggleAddOn = (id: string) => {
        setSelectedAddOns(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
    };

    return (
        <div className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" {...backdropClose(onClose)}>
            <div
                className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-8 pb-6 border-b border-zinc-100 rounded-t-[2.5rem]">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-[#aad816]/10 rounded-2xl">
                            <Calculator className="w-6 h-6 text-black" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-black text-zinc-900 tracking-tight">Cost Calculator</h3>
                            <p className="text-[9px] font-black text-zinc-400 uppercase tracking-[0.2em]">Yearly estimate · Pro vs Pro AI · GST included</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 rounded-2xl bg-zinc-50 border border-zinc-100 text-zinc-400 hover:text-black hover:bg-zinc-100 transition-all"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-8 space-y-8">
                    {/* Config */}
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Member Count */}
                            <div>
                                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-3">Number of members</p>
                                <NumberInput value={members} onChange={setMembers} className="w-full" />
                                {blocks > 0 && (
                                    <p className="mt-2 text-[10px] font-bold text-zinc-400">
                                        {BASE_MEMBER_LIMIT} included + {blocks} × {EXTRA_BLOCK_SIZE}-member block{blocks > 1 ? 's' : ''} @ ₹{EXTRA_BLOCK_YEARLY_PRICE}/yr
                                    </p>
                                )}
                            </div>

                            {/* Add-ons */}
                            <div>
                                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-3">Add-ons</p>
                                <div className="grid grid-cols-2 gap-3">
                                    {ADD_ONS.map(addOn => {
                                        const active = selectedAddOns.includes(addOn.id);
                                        return (
                                            <button
                                                key={addOn.id}
                                                onClick={() => toggleAddOn(addOn.id)}
                                                className={`flex items-center gap-3 p-4 rounded-2xl border text-left transition-all ${active
                                                    ? 'bg-[#aad816]/10 border-[#aad816] shadow-lg shadow-[#aad816]/10'
                                                    : 'bg-zinc-50 border-zinc-100 hover:bg-zinc-100'
                                                    }`}
                                            >
                                                <div className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all flex-shrink-0 ${active ? 'bg-[#aad816] border-[#aad816]' : 'bg-white border-zinc-200'}`}>
                                                    {active && <Check className="w-3.5 h-3.5 text-black" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-black text-zinc-900 tracking-tight">{addOn.name}</p>
                                                    <p className="text-[10px] font-bold text-zinc-400">₹{addOn.yearlyPrice.toLocaleString()}/yr</p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Additional Messages */}
                        <div>
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Expected additional messages / yr</p>
                                <span className="px-2 py-0.5 bg-black text-white text-[7px] font-black rounded-full uppercase tracking-widest">Beyond ₹{INCLUDED_CREDITS_VALUE.toLocaleString()} loaded credits</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100">
                                    <p className="text-xs font-black text-zinc-900 tracking-tight mb-1">Utility messages</p>
                                    <p className="text-[9px] font-black text-[#7a9b10] uppercase tracking-widest mb-2">₹{UTILITY_MSG_RATE.toFixed(2)} / msg</p>
                                    <NumberInput value={utilityMsgs} onChange={setUtilityMsgs} className="w-full" />
                                </div>
                                <div className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100">
                                    <p className="text-xs font-black text-zinc-900 tracking-tight mb-1">Marketing messages</p>
                                    <p className="text-[9px] font-black text-[#7a9b10] uppercase tracking-widest mb-2">₹{MARKETING_MSG_RATE.toFixed(2)} / msg</p>
                                    <NumberInput value={marketingMsgs} onChange={setMarketingMsgs} className="w-full" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Two Cost Lanes */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {PRICING_PLANS.map(plan => (
                            <PlanCostLane
                                key={plan.id}
                                plan={plan}
                                memberCount={memberCount}
                                selectedAddOns={selectedAddOns}
                                utilityMsgs={numOrZero(utilityMsgs)}
                                marketingMsgs={numOrZero(marketingMsgs)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function PricingShowcasePage() {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);

    return (
        <div className={`bg-white transition-all duration-500 ${isFullscreen ? 'fixed inset-0 z-[9999] overflow-y-auto pb-40' : 'min-h-screen'}`}>
            {/* Presentation Mode Toggle */}
            <div className="fixed bottom-10 right-10 z-[10000] flex flex-col items-end gap-3">
                <button
                    onClick={() => setCalculatorOpen(true)}
                    className="flex items-center gap-3 px-6 py-4 bg-[#aad816] text-black rounded-[2rem] shadow-2xl shadow-[#aad816]/40 hover:scale-105 active:scale-95 transition-all"
                >
                    <Calculator className="w-5 h-5" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Cost Calculator</span>
                </button>
                <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="flex items-center gap-3 px-6 py-4 bg-zinc-900 text-white rounded-[2rem] shadow-2xl shadow-zinc-900/40 border border-white/10 hover:scale-105 active:scale-95 transition-all group"
                >
                    {isFullscreen ? <Minimize className="w-5 h-5 text-zinc-400" /> : <Maximize className="w-5 h-5 text-zinc-400" />}
                    <span className="text-[10px] font-black uppercase tracking-widest">{isFullscreen ? 'Exit Full Page' : 'Enter Presentation Mode'}</span>
                </button>
            </div>

            <style>{`
                @keyframes gradient-x {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                .animate-gradient-x {
                    animation: gradient-x 6s ease infinite;
                }
            `}</style>

            {calculatorOpen && (
                <PricingCalculatorModal
                    isOpen={calculatorOpen}
                    onClose={() => setCalculatorOpen(false)}
                />
            )}

            {/* 1. Landing (Hero) Section */}
            <section className="relative pt-32 pb-40 overflow-hidden bg-white">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none">
                    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#aad816]/5 rounded-full blur-[120px] opacity-60"></div>
                    <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-zinc-100 rounded-full blur-[120px] opacity-60"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 mb-12">
                        <img
                            src="/gympe.svg"
                            alt="GYMPE Logo"
                            className="h-12 w-auto drop-shadow-sm hover:scale-105 transition-transform duration-300"
                        />
                    </div>

                    <h1 className="text-6xl md:text-8xl font-black text-zinc-900 tracking-tighter mb-8 bg-clip-text">
                        Scale your gym with <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-black via-black to-[#aad816]">Precision.</span>
                    </h1>

                    <p className="max-w-2xl mx-auto text-xl text-zinc-500 font-medium leading-relaxed">
                        The ultimate management ecosystem for professional fitness centers ready to dominate the market.
                    </p>
                </div>
            </section>

            {/* 2. Value Proposition Section */}
            <section className="py-40 bg-zinc-50 border-y border-zinc-100 relative">
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[10px] font-black text-[#aad816] uppercase tracking-[0.4em] mb-4">Value Proposition</p>
                        <h2 className="text-5xl font-black text-zinc-900 tracking-tighter">Engineered for Fitness Leaders.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {WHY_GYMPE.map((item, idx) => (
                            <div key={idx} className="group p-10 rounded-[3rem] bg-white border border-zinc-100 transition-all hover:shadow-2xl hover:shadow-zinc-200/50 hover:-translate-y-2">
                                <div className="w-16 h-16 bg-zinc-50 rounded-2xl border border-zinc-100 flex items-center justify-center mb-8 group-hover:scale-110 group-hover:bg-[#aad816]/10 transition-all">
                                    {item.icon}
                                </div>
                                <h4 className="text-2xl font-black text-zinc-900 mb-4 tracking-tight">{item.title}</h4>
                                <p className="text-sm text-zinc-500 font-medium leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. The Future Roadmap Section */}
            <section className="py-40 bg-black relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-[#aad816]/5 rounded-full blur-[150px]"></div>
                <div className="absolute bottom-0 left-0 w-[50%] h-[50%] bg-zinc-800/10 rounded-full blur-[150px]"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[10px] font-black text-[#aad816] uppercase tracking-[0.4em] mb-4">The Future Roadmap</p>
                        <h2 className="text-5xl font-black text-white tracking-tighter">What's coming next to Gympe.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {THE_ROADMAP.map((item, idx) => (
                            <div key={idx} className="p-10 rounded-[3rem] bg-white/5 border border-white/10 text-white relative overflow-hidden group hover:bg-white/[0.08] transition-all">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -mr-10 -mt-10 transition-all group-hover:scale-150"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="p-4 bg-white/10 rounded-2xl border border-white/10 group-hover:scale-110 transition-transform">
                                            {item.icon}
                                        </div>
                                        <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10">{item.date}</span>
                                    </div>
                                    <h4 className="text-2xl font-black mb-4 tracking-tight">{item.title}</h4>
                                    <p className="text-sm text-zinc-400 font-medium leading-relaxed">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 4. Pricing Section */}
            <section className="py-40 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center mb-20">
                        <p className="text-[10px] font-black text-[#aad816] uppercase tracking-[0.4em] mb-6">Commercial Tiers</p>
                        <h2 className="text-5xl font-black text-zinc-900 tracking-tighter mb-8">Choose the perfect plan.</h2>
                        <p className="text-lg text-zinc-500 font-medium">
                            Simple yearly billing. Every plan covers up to {BASE_MEMBER_LIMIT} members with ₹{INCLUDED_CREDITS_VALUE.toLocaleString()} notification credits loaded.
                        </p>
                        <p className="mt-3 text-xs font-bold text-zinc-400 uppercase tracking-widest">
                            All prices exclusive of {GST_PERCENT}% GST
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {PRICING_PLANS.map((plan) => (
                            <PricingCard
                                key={plan.id}
                                plan={plan}
                                onOpenCalculator={() => setCalculatorOpen(true)}
                            />
                        ))}
                    </div>

                    {/* Build Your Plan — Add-ons */}
                    <div className="mt-20 max-w-4xl mx-auto">
                        <div className="text-center mb-10">
                            <p className="text-[10px] font-black text-[#aad816] uppercase tracking-[0.4em] mb-4">Build Your Plan</p>
                            <h3 className="text-3xl font-black text-zinc-900 tracking-tighter">Optional add-ons.</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {ADD_ONS.map((addOn) => (
                                <div key={addOn.id} className="flex items-center gap-5 p-8 rounded-[2.5rem] bg-zinc-50 border border-zinc-100 transition-all hover:bg-zinc-100/50 hover:shadow-lg">
                                    <div className="p-4 bg-white rounded-2xl shadow-sm border border-zinc-100 flex-shrink-0">
                                        {addOn.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-black text-zinc-900 tracking-tight">{addOn.name}</h4>
                                        <p className="text-xs text-zinc-500 font-medium leading-relaxed mt-1">{addOn.description}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-xl font-black text-zinc-900 tracking-tight">₹{addOn.yearlyPrice.toLocaleString()}</p>
                                        <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">/yr + GST</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Messaging Rates Info */}
                    <div className="mt-16 max-w-4xl mx-auto p-12 bg-zinc-50 rounded-[3rem] border border-zinc-100 flex flex-col md:flex-row items-center gap-12 group transition-all hover:bg-zinc-100/50">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-white rounded-2xl shadow-sm border border-zinc-100">
                                    <MessageSquare className="w-6 h-6 text-[#aad816]" />
                                </div>
                                <h3 className="text-2xl font-black text-zinc-900 tracking-tight">Messaging & Campaigns</h3>
                            </div>
                            <p className="text-zinc-500 font-medium leading-relaxed">
                                Every plan comes with ₹{INCLUDED_CREDITS_VALUE.toLocaleString()} of WhatsApp notification credits loaded on activation.
                                Additional messages are billed by type — utility notifications like renewals and receipts, or marketing campaigns.
                            </p>
                        </div>
                        <div className="flex-shrink-0 grid grid-cols-2 gap-4">
                            <div className="bg-white p-6 rounded-[2rem] border border-zinc-100 shadow-sm text-center">
                                <p className="text-[10px] font-black text-zinc-400 uppercase mb-1">Utility Msg</p>
                                <p className="text-2xl font-black tracking-tight text-zinc-900">₹{UTILITY_MSG_RATE.toFixed(2)}</p>
                            </div>
                            <div className="bg-white p-6 rounded-[2rem] border border-zinc-100 shadow-sm text-center">
                                <p className="text-[10px] font-black text-zinc-400 uppercase mb-1">Marketing Msg</p>
                                <p className="text-2xl font-black tracking-tight text-zinc-900">₹{MARKETING_MSG_RATE.toFixed(2)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Future Scalability Note */}
                    <div className="mt-8 p-6 text-center">
                        <div className="inline-flex items-center gap-2 text-zinc-400">
                            <Shield className="w-4 h-4" />
                            <p className="text-xs font-bold uppercase tracking-widest">Enterprise SLA & Custom Onboarding Available for Groups</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
